'use client'

import { useCallback, useMemo, useState } from 'react'
import { useIsHydrated } from '@/hooks/use-is-hydrated'
import {
  clearTelemetryIdentifiers,
  denyConsent,
  getConsentState,
  grantConsent,
} from '@/lib/consent/client'
import type { ConsentDecision } from '@/lib/consent/signal'

/**
 * ConsentChoiceControl — where a gated visitor changes the choice the banner
 * asked for (FRIDGE-115). Lives in §4 of the privacy policy.
 *
 * Renders ONLY for visitors the middleware marked consent-gated, so the page
 * reads identically everywhere else and no promise is made to a visitor who
 * has no control. State is read from the cookie after mount, so the server
 * HTML is the same for everyone and stays cacheable.
 *
 * Withdrawing after an earlier Allow clears the identifiers the tags wrote
 * and reloads, so the page comes back with nothing running — otherwise the
 * already-initialised libraries would keep sending for the rest of the visit
 * and the control would be a half-truth.
 */
export function ConsentChoiceControl() {
  // The cookie is read AFTER hydration and derived, rather than pushed into
  // state from an effect — the shape react-hooks/set-state-in-effect flags,
  // because it renders a known-empty value and then re-renders.
  //
  // The contract in the doc comment above is unchanged: nothing is read on the
  // server, so the server HTML is identical for everyone and stays cacheable.
  //
  // `decision` still needs to be writable, because Allow and Withdraw change
  // it from event handlers. That lives in `chosen`, which overrides the cookie
  // value once the visitor has acted in this session.
  const hydrated = useIsHydrated()
  const cookieState = useMemo(() => (hydrated ? getConsentState() : null), [hydrated])
  const [chosen, setChosen] = useState<ConsentDecision | null>(null)
  const gated = cookieState?.gated ?? false
  const decision = chosen ?? cookieState?.decision ?? null

  const allow = useCallback(() => {
    grantConsent()
    setChosen('granted')
  }, [])

  const withdraw = useCallback(() => {
    const hadConsent = getConsentState().decision === 'granted'
    denyConsent()
    setChosen('denied')
    if (hadConsent) {
      clearTelemetryIdentifiers()
      window.location.reload()
    }
  }, [])

  if (!gated) return null

  const label =
    decision === 'granted'
      ? 'Analytics are allowed for this browser.'
      : decision === 'denied'
        ? 'Analytics are switched off for this browser.'
        : 'You have not chosen yet — analytics are switched off until you do.'

  return (
    <div
      data-testid="consent-choice-control"
      style={{
        marginTop: 16,
        padding: 16,
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        background: '#ffffff',
      }}
    >
      <p style={{ margin: 0, fontSize: 14, color: '#374151' }}>{label}</p>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          type="button"
          onClick={allow}
          disabled={decision === 'granted'}
          data-testid="consent-control-allow"
          style={{
            borderRadius: 8,
            padding: '8px 16px',
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--color-brand-action-text)',
            background: '#ff8533',
            cursor: decision === 'granted' ? 'not-allowed' : 'pointer',
            border: 'none',
          }}
        >
          Allow analytics
        </button>
        <button
          type="button"
          onClick={withdraw}
          disabled={decision === 'denied'}
          data-testid="consent-control-withdraw"
          style={{
            borderRadius: 8,
            padding: '8px 16px',
            fontSize: 14,
            fontWeight: 600,
            color: '#374151',
            background: '#ffffff',
            border: '1px solid #d1d5db',
            opacity: decision === 'denied' ? 0.6 : 1,
          }}
        >
          Switch analytics off
        </button>
      </div>
    </div>
  )
}

export default ConsentChoiceControl
